import {onIdTokenChanged} from 'firebase/auth';

import {attachmentSelector, createProtectedFileClient, useProtectedFileUrl as useProtectedFileUrlFor} from '@embertracking/ui/data/protectedFiles';

import {auth, projectId} from '-/firebase';
import {resolveApiBase} from '-/data/apiBase';
import {isOfflineError} from '-/data/errorReporting';

export const protectedFileClient = createProtectedFileClient({
    getToken: () => auth.currentUser?.getIdToken(),
    getApiBase: resolveApiBase,
    getRequestHeaders: () => ({'X-Project-Id': projectId})
});

let stopSessionObserver;
let stopFocusObserver;
export const observeProtectedFileSession = () => {
    if (!stopSessionObserver) {
        stopSessionObserver = onIdTokenChanged(auth, user => {
            protectedFileClient.setSession(user ? `${projectId}:${user.uid}` : null);
        });
        if (typeof window !== 'undefined') {
            // Refreshes stream credentials; cached previews are kept (#2088).
            const refreshOnFocus = () => protectedFileClient.refreshSession();
            window.addEventListener('focus', refreshOnFocus);
            stopFocusObserver = () => window.removeEventListener('focus', refreshOnFocus);
        }
    }
    return () => {
        stopSessionObserver?.();
        stopFocusObserver?.();
        stopSessionObserver = null;
        stopFocusObserver = null;
    };
};

export const selectorForFile = (file, recordPath, variant) => attachmentSelector(file, recordPath, variant);

export const useProtectedFileUrl = (file, recordPath, variant, options) =>
    useProtectedFileUrlFor(protectedFileClient, selectorForFile(file, recordPath, variant), options);

export const fetchProtectedFileUrl = async (file, recordPath, variant, options) => {
    const selector = selectorForFile(file, recordPath, variant);
    if (!selector) return null;
    return protectedFileClient.objectUrl(selector, options);
};

export const protectedFileStreamUrl = async (file, recordPath, variant) => {
    const selector = selectorForFile(file, recordPath, variant);
    if (!selector) throw new Error('File is missing its authenticated attachment identity');
    return await protectedFileClient.streamUrl(selector);
};

// streamUrl only authorizes the service worker; the download itself happens in
// the new tab. Expected refusals here are a signed-out session, a request
// superseded by a session change, a browser without service workers (e.g.
// private browsing) and a dropped connection. Everything else, including an
// attachment with no identity or a worker that never takes control, is a fault
// worth reporting (#2050).
export const isExpectedStreamError = error => {
    if (error?.code === 'unauthenticated' || error?.code === 'aborted') return true;
    if (error?.code === 'transport-unavailable'
        && (typeof navigator === 'undefined' || !navigator.serviceWorker || typeof MessageChannel === 'undefined')) {
        return true;
    }
    return isOfflineError(error);
};

export const fetchProtectedFile = (file, recordPath, variant, options) => {
    const selector = selectorForFile(file, recordPath, variant);
    if (!selector) throw new Error('File is missing its authenticated attachment identity');
    return protectedFileClient.fetch(selector, options);
};
